import React, { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import styled from 'styled-components';
import { Confirmation_, Grey_, Input_, Main_Plain, Partial_Loader, Slider_ } from 'monica-alexandria';
import { checkPermissions } from '../../../helpers/checkPermissions';
import { get_pending_merge, merge_records, preview_merge_records, search_merge_records } from '../../../services/api/Merges';
import Merge_Preview from './Merge_Preview';

const MergeRecords = styled.div`
    display: grid;
    gap: 2rem;
    min-width: 0;
    color: ${p => p.theme.color};
`;

const MergeRecordsCard = styled.div`
    display: grid;
    gap: 1rem;
    padding: var(--normalPads);
    border-radius: var(--smallRadius);
    background: ${p => p.theme.background};

    p, h5, h6 {
        overflow-wrap: anywhere;
    }
`;

export default function Merge_Records(props) {
    const [searchTerm, setSearchTerm] = useState('');
    const [search] = useDebounce(searchTerm, 400);
    const [candidates, setCandidates] = useState([]);
    const [selectedCandidates, setSelectedCandidates] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searched, setSearched] = useState(false);
    const [preview, setPreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [choices, setChoices] = useState({});
    const [confirming, setConfirming] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [searchRetry, setSearchRetry] = useState(0);
    const [previewIdentity, setPreviewIdentity] = useState(null);
    const [checkingPending, setCheckingPending] = useState(true);
    const [pendingChecked, setPendingChecked] = useState(false);
    const [pendingRetry, setPendingRetry] = useState(0);
    const requestRef = useRef(0);
    const savingRef = useRef(false);
    const hasPermission = checkPermissions('King');
    const isLead = props.entityType === 'lead';
    const selectedRecords = Array.isArray(props.records) ? props.records : [];
    const hasFixedSelection = selectedRecords.length > 1;
    const fixedSourceIds = selectedRecords.filter(record => record.id !== props.record.id).map(record => record.id);
    const fixedSelectionKey = fixedSourceIds.join(',');
    const previewSources = preview ? preview.sources || [preview.source] : [];
    const pendingRecordIds = preview?.resume ? [preview.survivor, ...previewSources].map(record => record.id) : [];
    const pendingMatchesSelection = hasFixedSelection
        && pendingRecordIds.length === selectedRecords.length
        && pendingRecordIds.every(id => selectedRecords.some(record => record.id === id));
    const resumeBeforeSelection = hasFixedSelection && preview?.resume && !pendingMatchesSelection;
    const unresolved = preview?.fields.some(field => field.conflict && !choices[field.key]);
    const canMerge = hasPermission && preview && !unresolved && !preview.blockers.length && !saving && !loadingPreview;
    let actionText = 'Review merge';
    let confirmationText = 'Merge records';
    if (preview?.resume) {
        actionText = 'Resume merge';
        confirmationText = 'Resume merge';
    }
    if (saving) {
        actionText = 'Merging records';
        confirmationText = 'Merging records';
    }

    useEffect(() => {
        let active = true;
        if (!hasPermission) {
            setCheckingPending(false);
            return;
        }
        setCheckingPending(true);
        setError('');
        get_pending_merge(props.entityType, props.record.id).then(pending => {
            if (!active) {
                return;
            }
            if (pending) {
                setPreview(pending);
                setPreviewIdentity({ survivorId: pending.survivor.id, sourceIds: (pending.sources || [pending.source]).map(source => source.id) });
                setChoices(pending.choices);
            }
            setPendingChecked(true);
        }).catch(error => {
            if (active) {
                setError(error.response?.data?.message || 'There was an error checking for an unfinished merge');
            }
        }).finally(() => {
            if (active) {
                setCheckingPending(false);
            }
        });
        return () => { active = false; };
    }, [hasPermission, props.entityType, props.record.id, pendingRetry]);

    useEffect(() => {
        let active = true;
        if (!hasPermission || hasFixedSelection || !pendingChecked || !search.trim() || previewIdentity) {
            setCandidates([]);
            setSearching(false);
            setSearched(false);
            return;
        }
        setSearching(true);
        setError('');
        search_merge_records(props.entityType, search.trim(), props.record.id).then(records => {
            if (active) {
                setCandidates(records);
                setSearched(true);
            }
        }).catch(error => {
            if (active) {
                setError(error.response?.data?.message || 'There was an error searching merge candidates');
                setSearched(false);
            }
        }).finally(() => {
            if (active) {
                setSearching(false);
            }
        });
        return () => { active = false; };
    }, [search, searchRetry, hasPermission, hasFixedSelection, pendingChecked, previewIdentity, props.entityType, props.record.id]);

    useEffect(() => () => { requestRef.current += 1; }, []);

    const load_preview = async (survivorId, sourceIds, message = '') => {
        if (savingRef.current || !hasPermission || preview?.resume) {
            return;
        }
        const requestId = ++requestRef.current;
        const ids = Array.isArray(sourceIds) ? sourceIds : [sourceIds];
        setPreviewIdentity({ survivorId, sourceIds: ids });
        setLoadingPreview(true);
        setPreview(null);
        setChoices({});
        setConfirming(false);
        setError(message);
        try {
            const result = await preview_merge_records(props.entityType, survivorId, ids.length === 1 ? ids[0] : ids);
            if (requestId === requestRef.current) {
                setPreview(result);
            }
        } catch (error) {
            if (requestId === requestRef.current) {
                setError(error.response?.data?.message || 'There was an error preparing the merge');
            }
        } finally {
            if (requestId === requestRef.current) {
                setLoadingPreview(false);
            }
        }
    };

    const reset_candidate = () => {
        if (savingRef.current || preview?.resume) {
            return;
        }
        requestRef.current += 1;
        setPreview(null);
        setPreviewIdentity(null);
        setLoadingPreview(false);
        setChoices({});
        setConfirming(false);
        setError('');
        setNotice('');
    };

    useEffect(() => {
        if (!hasFixedSelection || !hasPermission || !pendingChecked || checkingPending || previewIdentity || !fixedSourceIds.length) {
            return;
        }

        load_preview(props.record.id, fixedSourceIds);
    }, [hasFixedSelection, hasPermission, pendingChecked, checkingPending, previewIdentity, props.entityType, props.record.id, fixedSelectionKey]);

    const close_merge = () => {
        if (!savingRef.current) {
            props.onClose();
        }
    };

    const submit_merge = async () => {
        if (!canMerge || savingRef.current) {
            return;
        }
        savingRef.current = true;
        setSaving(true);
        setError('');
        try {
            const result = await merge_records(preview, choices);
            if (resumeBeforeSelection) {
                setPreview(null);
                setPreviewIdentity(null);
                setChoices({});
                setConfirming(false);
                setNotice('The earlier merge is complete. Review your selected records before merging them.');
            } else {
                props.onMerged(result);
            }
        } catch (error) {
            setConfirming(false);
            const message = error.response?.data?.message || 'There was an error merging these records';
            const preserveReview = ['MERGE_RETRY_REQUIRED', 'MERGE_ALREADY_RUNNING', 'MERGE_LEASE_LOST'].includes(error.response?.data?.code) || !error.response;
            if (error.response?.status === 409 && !preserveReview && !preview.resume) {
                savingRef.current = false;
                setSaving(false);
                await load_preview(preview.survivor.id, previewSources.map(source => source.id), message);
            } else {
                if (preserveReview) {
                    setPreview(current => ({ ...current, resume: true }));
                }
                setError(message);
            }
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    return (
        <Slider_
            title={props.t(props.entityType === 'lead' ? 'Merge leads' : 'Merge brands')}
            text={props.t(actionText)}
            disabled={!canMerge}
            onClick={() => { if (canMerge) setConfirming(true); }}
            grey={props.t('Cancel')}
            onGrey={close_merge}
            onClose={close_merge}
        >
            <MergeRecords>
                {!hasPermission ? (
                    <p role="alert">{props.t('Only Kings can merge records during testing.')}</p>
                ) : hasFixedSelection ? (
                    <MergeRecordsCard>
                        <h5>{props.t(isLead ? 'Selected leads ({{count}})' : 'Selected brands ({{count}})', { count: selectedRecords.length })}</h5>
                        {selectedRecords.map(record => <p key={record.id}>{record.label} · {record.id}</p>)}
                        {isLead ? <p>{props.t('A registered selected lead is kept to preserve its account identity; otherwise, the oldest selected lead is kept.')}</p> : <p>{props.t('Choose the Brand to keep during review.')}</p>}
                    </MergeRecordsCard>
                ) : (
                    <MergeRecordsCard>
                        <h5>{props.t('Selected record')}</h5>
                        <p>{props.record.label} · {props.record.id}</p>
                        {!previewIdentity && pendingChecked ? (
                            <Input_ aria-label={props.t('Search duplicates')} placeholder={props.t('Search duplicates')} value={searchTerm} onChange={event => setSearchTerm(event.target.value)}></Input_>
                        ) : null}
                        {previewIdentity && !preview?.resume ? <Grey_ text={props.t(isLead ? 'Change selected leads' : 'Choose another duplicate')} onClick={reset_candidate}></Grey_> : null}
                    </MergeRecordsCard>
                )}
                {checkingPending ? <Partial_Loader text={props.t('Checking for an unfinished merge')}></Partial_Loader> : null}
                {searching ? <Partial_Loader text={props.t('Searching duplicates')}></Partial_Loader> : null}
                {hasPermission && !hasFixedSelection && pendingChecked && !search.trim() && !previewIdentity ? <p>{props.t(isLead ? 'Search by lead name, brand, email or phone. Select all duplicates to merge.' : 'Search by name, email or phone to choose the duplicate.')}</p> : null}
                {!hasFixedSelection && isLead && pendingChecked && !previewIdentity && selectedCandidates.length > 0 ? (
                    <MergeRecordsCard>
                        <h5>{props.t('Selected leads ({{count}})', { count: selectedCandidates.length + 1 })}</h5>
                        <p>{props.record.label} · {props.record.id}</p>
                        {selectedCandidates.map(candidate => (
                            <div key={candidate.id}>
                                <p>{candidate.label} · {candidate.id}</p>
                                <Grey_ text={props.t('Remove {{lead}} from selection', { lead: candidate.label })} onClick={() => setSelectedCandidates(current => current.filter(record => record.id !== candidate.id))}></Grey_>
                            </div>
                        ))}
                        <Main_Plain text={props.t('Preview selected leads')} onClick={() => load_preview(props.record.id, selectedCandidates.map(candidate => candidate.id))}></Main_Plain>
                    </MergeRecordsCard>
                ) : null}
                {!hasFixedSelection && searched && !searching && !previewIdentity && candidates.length === 0 ? <p>{props.t(isLead ? 'No matching duplicates found. Try another lead name, brand, email or phone.' : 'No matching duplicates found. Try another name, email or phone.')}</p> : null}
                {!hasFixedSelection && !previewIdentity && !searching ? candidates.filter(candidate => !selectedCandidates.some(selected => selected.id === candidate.id)).map(candidate => (
                    <MergeRecordsCard key={candidate.id}>
                        <h5>{candidate.label}</h5>
                        <p>{candidate.subtitle}</p>
                        {candidate.brands?.length ? <p>{props.t('Brands')}: {candidate.brands.map(brand => brand.label).join(' · ')}</p> : null}
                        <h6>{candidate.id}</h6>
                        <Main_Plain text={props.t('Select duplicate')} onClick={() => isLead ? setSelectedCandidates(current => current.some(record => record.id === candidate.id) ? current : [...current, candidate]) : load_preview(props.record.id, candidate.id)}></Main_Plain>
                    </MergeRecordsCard>
                )) : null}
                {loadingPreview ? <Partial_Loader text={props.t('Preparing merge preview')}></Partial_Loader> : null}
                {error ? (
                    <MergeRecordsCard role="alert">
                        <p>{props.t(error)}</p>
                        {!previewIdentity && pendingChecked ? <Grey_ text={props.t('Retry')} onClick={() => setSearchRetry(value => value + 1)}></Grey_> : null}
                        {!pendingChecked && !checkingPending ? <Grey_ text={props.t('Retry')} onClick={() => setPendingRetry(value => value + 1)}></Grey_> : null}
                        {previewIdentity && !preview && !loadingPreview ? <Grey_ text={props.t('Retry preview')} onClick={() => load_preview(previewIdentity.survivorId, previewIdentity.sourceIds)}></Grey_> : null}
                    </MergeRecordsCard>
                ) : null}
                {notice ? <MergeRecordsCard role="status"><p>{props.t(notice)}</p></MergeRecordsCard> : null}
                {resumeBeforeSelection ? (
                    <MergeRecordsCard role="alert">
                        <p>{props.t('An earlier merge involving one selected record must finish first. This operation absorbs {{source}} into {{survivor}}. Your selected records will be reviewed afterwards.', {
                            source: previewSources.map(source => source.label).join(', '), survivor: preview.survivor.label
                        })}</p>
                    </MergeRecordsCard>
                ) : null}
                {preview?.resume ? <p>{props.t('An unfinished merge is ready to resume. The reviewed records and field choices are preserved.')}</p> : null}
                {preview ? (
                    <Merge_Preview
                        preview={preview}
                        choices={choices}
                        t={props.t}
                        disabled={saving || preview.resume}
                        lockSurvivor={props.lockSurvivor}
                        onChoice={(key, value) => setChoices(current => ({ ...current, [key]: value }))}
                        onSurvivorChange={id => { if (id !== preview.survivor.id) load_preview(id, [preview.survivor, ...previewSources].filter(record => record.id !== id).map(record => record.id)); }}
                    ></Merge_Preview>
                ) : null}
                {unresolved ? <p>{props.t('Choose a value for every conflicting field before reviewing the merge.')}</p> : null}
                {saving ? <Partial_Loader text={props.t('Merging records')}></Partial_Loader> : null}
                {confirming ? (
                    <Confirmation_
                        message={previewSources.length > 1
                            ? props.t('Merge {{count}} duplicates into {{survivor}} and remove them from active lists?', { count: previewSources.length, survivor: preview.survivor.label })
                            : props.t('Merge {{source}} into {{survivor}} and remove the duplicate from active lists?', { source: preview.source.label, survivor: preview.survivor.label })}
                        yes={props.t(confirmationText)}
                        no={props.t('Go back')}
                        yesDisabled={saving}
                        onYes={submit_merge}
                        onNo={() => { if (!savingRef.current) setConfirming(false); }}
                        onClose={() => { if (!savingRef.current) setConfirming(false); }}
                    ></Confirmation_>
                ) : null}
            </MergeRecords>
        </Slider_>
    );
}
